// ============================================
// CAP.18 D#5 — Processing Delayed Notification
//
// Patient-facing notification shown when lab analysis is
// queued for retry (primary model unavailable). Key rules:
//   c1: Says "delayed" — never "failed"
//   c2: Includes estimated processing time
//   c3: Rendered immediately when delay is detected
//   c4: No false promise about results being ready
//
// Spec: specs/bones/captain/hlx-cap-18-fallback-validation.md
// ============================================

import { Clock, RefreshCw } from 'lucide-react';

interface ProcessingDelayedNotificationProps {
  /** Estimated wait time shown to the patient */
  estimatedMinutes?: number;
  /** Called when the patient clicks "Upload different labs" */
  onUploadDifferent?: () => void;
}

/**
 * Patient-facing "processing delayed" notification.
 * Shown instead of an error when labs are queued for retry.
 */
export function ProcessingDelayedNotification({
  estimatedMinutes = 15,
  onUploadDifferent,
}: ProcessingDelayedNotificationProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="w-full p-5 rounded-lg bg-amber-50 border border-amber-200 text-center"
      style={{ transition: 'opacity 0.3s ease' }}
    >
      {/* Icon */}
      <div className="flex justify-center mb-3">
        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
          <Clock size={20} className="text-amber-600" />
        </div>
      </div>

      {/* Heading — delayed, not failed (c1) */}
      <h3
        className="font-semibold text-[#18181B]"
        style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '15px' }}
      >
        Your results are being processed
      </h3>

      {/* Estimated time (c2) + no false promise (c4) */}
      <p
        className="mt-2 text-[#52525B]"
        style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', lineHeight: '1.5' }}
      >
        Our analysis system is experiencing higher than usual demand.
        Your labs have been queued and will be processed automatically.
      </p>

      {/* Estimated time badge (c2) */}
      <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-100 text-amber-700">
        <RefreshCw size={13} className="animate-spin" style={{ animationDuration: '3s' }} />
        <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: 500 }}>
          Estimated wait: ~{estimatedMinutes} minutes
        </span>
      </div>

      {/* No false promise — explicit that we'll keep trying, not that it's ready (c4) */}
      <p
        className="mt-3 text-[#71717A]"
        style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '13px' }}
      >
        We&rsquo;ll keep retrying automatically. You can safely close this page
        and check back later.
      </p>

      {/* Fallback action */}
      {onUploadDifferent && (
        <button
          onClick={onUploadDifferent}
          className="mt-4 text-[13px] text-[#71717A] underline hover:text-[#18181B]"
          style={{ fontFamily: 'DM Sans, sans-serif', transition: 'color 0.2s ease' }}
        >
          Upload different labs instead
        </button>
      )}
    </div>
  );
}
